// @ts-nocheck -- 开发期模拟数据不参与生产 API 契约校验
/**
 * 商品域 Mock（类目 / 商品 / SKU / Banner / 评价）
 */

import type { MockAdapter } from 'axios-mock-adapter';
import { ProductStatus } from '@/shared';
import {
  type Product,
  type ProductCategory,
  type Banner,
  type Review,
} from '@gyjk/shared';

const traceId = () => `mock-${Date.now()}`;
const ok = (data: unknown) => [
  200,
  { success: true, code: 'OK', message: 'ok', data, traceId: traceId() },
];
const fail = (message: string) => [
  400,
  { success: false, code: 'BAD_REQUEST', message, data: null, traceId: traceId() },
];

// ───── 类目 ─────
const categories: ProductCategory[] = [
  {
    id: 'cat-1',
    parentId: null,
    name: '营养保健',
    icon: 'Food',
    sort: 1,
    status: 'ONLINE',
    createdAt: '2026-06-01T00:00:00.000Z',
  },
  {
    id: 'cat-2',
    parentId: null,
    name: '医疗器械',
    icon: 'FirstAidKit',
    sort: 2,
    status: 'ONLINE',
    createdAt: '2026-06-01T00:00:00.000Z',
  },
  {
    id: 'cat-3',
    parentId: 'cat-1',
    name: '维生素',
    icon: 'Grape',
    sort: 1,
    status: 'ONLINE',
    createdAt: '2026-06-01T00:00:00.000Z',
  },
];

function buildCategoryTree(list: ProductCategory[]): ProductCategory[] {
  const map = new Map<string, ProductCategory>();
  const roots: ProductCategory[] = [];
  list.forEach((c) => {
    map.set(c.id, { ...c, children: [] });
  });
  map.forEach((c) => {
    if (c.parentId && map.has(c.parentId)) {
      map.get(c.parentId)!.children!.push(c);
    } else {
      roots.push(c);
    }
  });
  return roots.sort((a, b) => a.sort - b.sort);
}

// ───── 商品 ─────
const products: Product[] = [
  {
    id: 'p-1',
    categoryId: 'cat-3',
    categoryName: '维生素',
    name: '复合维生素片 60粒',
    brand: '国优',
    mainImage: 'https://via.placeholder.com/120x120?text=Vitamin',
    images: [],
    tags: ['热销', '新品'],
    skus: [
      {
        id: 'sku-1',
        productId: 'p-1',
        skuCode: 'VIT-60-001',
        specNames: ['规格'],
        specValues: ['60粒/瓶'],
        price: 1990,
        originalPrice: 2990,
        stock: 120,
        salesCount: 30,
        status: ProductStatus.ONLINE,
      },
    ],
    minPrice: 1990,
    maxPrice: 1990,
    totalStock: 120,
    totalSales: 30,
    rating: 4.8,
    ratingCount: 12,
    status: ProductStatus.ONLINE,
    isHot: true,
    isNew: true,
    createdAt: '2026-06-10T00:00:00.000Z',
    updatedAt: '2026-06-10T00:00:00.000Z',
  },
  {
    id: 'p-2',
    categoryId: 'cat-2',
    categoryName: '医疗器械',
    name: '电子血压计',
    brand: 'Omron',
    mainImage: 'https://via.placeholder.com/120x120?text=BP',
    images: [],
    tags: ['医用级'],
    skus: [
      {
        id: 'sku-2',
        productId: 'p-2',
        skuCode: 'BP-001',
        specNames: ['规格'],
        specValues: ['标准版'],
        price: 12900,
        originalPrice: 15900,
        stock: 8,
        salesCount: 5,
        status: ProductStatus.ONLINE,
      },
    ],
    minPrice: 12900,
    maxPrice: 12900,
    totalStock: 8,
    totalSales: 5,
    rating: 4.6,
    ratingCount: 3,
    status: ProductStatus.ONLINE,
    isHot: false,
    isNew: false,
    createdAt: '2026-06-12T00:00:00.000Z',
    updatedAt: '2026-06-12T00:00:00.000Z',
  },
];

const stockLogs: Array<{
  id: string;
  productId: string;
  skuId: string;
  skuCode: string;
  change: number;
  stock: number;
  reason: string;
  createdAt: string;
}> = [];

const stockThresholds = new Map<string, number>();

// ───── 运费模板(默认与 061 种子一致:全场免运费)─────
const shippingTemplates = [
  {
    id: 'st-1',
    name: '默认运费模板',
    defaultFee: 0,
    freeThreshold: null as number | null,
    regionRules: [] as Array<{ provinces: string[]; fee: number; freeThreshold?: number | null }>,
    isDefault: true,
    createdAt: '2026-06-01T00:00:00.000Z',
    updatedAt: '2026-06-01T00:00:00.000Z',
  },
];

// ───── Banner ─────
const banners: Banner[] = [
  {
    id: 'b-1',
    title: '夏季养生节',
    image: 'https://via.placeholder.com/400x120?text=Banner1',
    link: '/pages/product/detail?id=p-1',
    linkType: 'PRODUCT',
    sort: 1,
    status: 'ONLINE',
    clickCount: 120,
    createdAt: '2026-06-01T00:00:00.000Z',
  },
];

// ───── 评价 ─────
const reviews: Review[] = [
  {
    id: 'r-1',
    orderId: 'o-1',
    productId: 'p-1',
    skuId: 'sku-1',
    memberId: 'm-1',
    memberNickname: '张阿姨',
    rating: 5,
    content: '效果很好，会继续回购。',
    images: [],
    isAnonymous: false,
    status: 'VISIBLE',
    createdAt: '2026-06-28T10:00:00.000Z',
  },
  {
    id: 'r-2',
    orderId: 'o-2',
    productId: 'p-2',
    skuId: 'sku-2',
    memberId: 'm-2',
    memberNickname: '匿名用户',
    rating: 3,
    content: '物流有点慢。',
    images: [],
    isAnonymous: true,
    status: 'VISIBLE',
    createdAt: '2026-06-29T10:00:00.000Z',
  },
];

function paginate<T>(list: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

function findProduct(id: string): Product | undefined {
  return products.find((p) => p.id === id);
}

function recalcProductStock(product: Product) {
  product.totalStock = product.skus.reduce((sum, s) => sum + (s.stock || 0), 0);
}

export function setup(mock: MockAdapter): void {
  // ───── 类目 ─────
  mock.onGet('/admin/categories').reply(() => ok(buildCategoryTree(categories)));

  mock.onPost('/admin/categories').reply((config) => {
    const body = JSON.parse(config.data || '{}');
    const item: ProductCategory = {
      id: `cat-${Date.now()}`,
      parentId: body.parentId ?? null,
      name: body.name,
      icon: body.icon || '',
      sort: body.sort ?? 0,
      status: body.status || 'ONLINE',
      createdAt: new Date().toISOString(),
    };
    categories.push(item);
    return ok(item);
  });

  mock.onPut(/\/admin\/categories\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const body = JSON.parse(config.data || '{}');
    const idx = categories.findIndex((c) => c.id === id);
    if (idx === -1) return fail('类目不存在');
    categories[idx] = { ...categories[idx], ...body };
    return ok(categories[idx]);
  });

  mock.onDelete(/\/admin\/categories\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const idx = categories.findIndex((c) => c.id === id);
    if (idx === -1) return fail('类目不存在');
    if (categories.some((c) => c.parentId === id)) return fail('请先删除子类目');
    categories.splice(idx, 1);
    return ok(null);
  });

  // ───── 商品 ─────
  mock.onGet('/admin/products').reply((config) => {
    const params = config.params || {};
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 20;
    const keyword = (params.keyword || '').toLowerCase();
    const status = params.status;
    let list = products;
    if (keyword) {
      list = list.filter((p) => p.name.toLowerCase().includes(keyword) || p.brand?.toLowerCase().includes(keyword));
    }
    if (status) list = list.filter((p) => p.status === status);
    return ok({ list: paginate(list, page, pageSize), total: list.length });
  });

  mock.onGet(/\/admin\/products\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const p = findProduct(id);
    return p ? ok(p) : fail('商品不存在');
  });

  mock.onPost('/admin/products').reply((config) => {
    const body = JSON.parse(config.data || '{}');
    const skus = body.skus || [];
    const product: Product = {
      id: `p-${Date.now()}`,
      ...body,
      minPrice: Math.min(...skus.map((s: { price: number }) => s.price)),
      maxPrice: Math.max(...skus.map((s: { price: number }) => s.price)),
      totalStock: skus.reduce((sum: number, s: { stock?: number }) => sum + (s.stock || 0), 0),
      totalSales: 0,
      rating: 5,
      ratingCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    products.unshift(product);
    return ok(product);
  });

  mock.onPut(/\/admin\/products\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const idx = products.findIndex((p) => p.id === id);
    if (idx === -1) return fail('商品不存在');
    const body = JSON.parse(config.data || '{}');
    products[idx] = { ...products[idx], ...body, updatedAt: new Date().toISOString() };
    recalcProductStock(products[idx]);
    return ok(products[idx]);
  });

  mock.onPut(/\/admin\/products\/([^/]+)\/status/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const p = findProduct(id);
    if (!p) return fail('商品不存在');
    const body = JSON.parse(config.data || '{}');
    p.status = body.status;
    p.updatedAt = new Date().toISOString();
    return ok(p);
  });

  mock.onPut(/\/admin\/products\/([^/]+)\/stock/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const p = findProduct(id);
    if (!p) return fail('商品不存在');
    const body = JSON.parse(config.data || '{}');
    const sku = p.skus.find((s) => s.id === body.skuId);
    if (!sku) return fail('SKU 不存在');
    const oldStock = sku.stock;
    sku.stock = body.stock ?? sku.stock;
    recalcProductStock(p);
    p.updatedAt = new Date().toISOString();
    stockLogs.unshift({
      id: `log-${Date.now()}`,
      productId: p.id,
      skuId: sku.id,
      skuCode: sku.skuCode,
      change: sku.stock - oldStock,
      stock: sku.stock,
      reason: body.reason || '后台调整',
      createdAt: new Date().toISOString(),
    });
    if (body.threshold !== undefined) stockThresholds.set(p.id, body.threshold);
    return ok(p);
  });

  // ───── Banner ─────
  mock.onGet('/admin/banners').reply(() => ok([...banners]));

  mock.onPost('/admin/banners').reply((config) => {
    const body = JSON.parse(config.data || '{}');
    const banner: Banner = {
      id: `b-${Date.now()}`,
      title: body.title,
      image: body.image,
      link: body.link,
      linkType: body.linkType || 'NONE',
      sort: body.sort ?? 0,
      status: body.status || 'ONLINE',
      clickCount: 0,
      createdAt: new Date().toISOString(),
    };
    banners.push(banner);
    return ok(banner);
  });

  mock.onPut(/\/admin\/banners\/([^/]+)\/status/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const banner = banners.find((b) => b.id === id);
    if (!banner) return fail('Banner 不存在');
    const body = JSON.parse(config.data || '{}');
    banner.status = body.status;
    return ok(banner);
  });

  // ───── 评价 ─────
  mock.onGet(/\/products\/([^/]+)\/reviews/).reply((config) => {
    const productId = (config.url as string).split('/')[2];
    const params = config.params || {};
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 20;
    const list = reviews.filter((r) => r.productId === productId);
    return ok({ list: paginate(list, page, pageSize), total: list.length });
  });

  mock.onGet('/admin/reviews').reply((config) => {
    const params = config.params || {};
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 20;
    const status = params.status;
    const keyword = (params.keyword || '').toLowerCase();
    let list = reviews;
    if (status) list = list.filter((r) => r.status === status);
    if (keyword) list = list.filter((r) => r.content.includes(keyword) || r.memberNickname.includes(keyword));
    return ok({ list: paginate(list, page, pageSize), total: list.length });
  });

  mock.onPost(/\/admin\/reviews\/([^/]+)\/reply/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const review = reviews.find((r) => r.id === id);
    if (!review) return fail('评价不存在');
    const body = JSON.parse(config.data || '{}');
    review.reply = body.reply;
    review.replyAt = new Date().toISOString();
    return ok(review);
  });

  mock.onPut(/\/admin\/reviews\/([^/]+)\/status/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const review = reviews.find((r) => r.id === id);
    if (!review) return fail('评价不存在');
    const body = JSON.parse(config.data || '{}');
    review.status = body.status;
    return ok(review);
  });

  // ───── 库存流水（内部使用）─────
  mock.onGet('/admin/stock/logs').reply((config) => {
    const params = config.params || {};
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 20;
    return ok({ list: paginate(stockLogs, page, pageSize), total: stockLogs.length });
  });

  // ───── 运费模板 ─────
  mock.onGet('/admin/shipping-templates').reply(() =>
    ok(shippingTemplates.map((t) => ({ ...t, productCount: products.filter((p: any) => p.shippingTemplateId === t.id).length }))),
  );

  mock.onPost('/admin/shipping-templates').reply((config) => {
    const body = JSON.parse(config.data || '{}');
    const item = {
      id: `st-${Date.now()}`,
      name: body.name,
      defaultFee: body.defaultFee ?? 0,
      freeThreshold: body.freeThreshold ?? null,
      regionRules: body.regionRules || [],
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    shippingTemplates.push(item);
    return ok(item);
  });

  mock.onPut(/\/admin\/shipping-templates\/([^/]+)\/default/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const target = shippingTemplates.find((t) => t.id === id);
    if (!target) return fail('运费模板不存在');
    shippingTemplates.forEach((t) => { t.isDefault = t.id === id; });
    return ok({ id, isDefault: true });
  });

  mock.onPut(/\/admin\/shipping-templates\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const idx = shippingTemplates.findIndex((t) => t.id === id);
    if (idx === -1) return fail('运费模板不存在');
    const body = JSON.parse(config.data || '{}');
    shippingTemplates[idx] = { ...shippingTemplates[idx], ...body, updatedAt: new Date().toISOString() };
    return ok(shippingTemplates[idx]);
  });

  mock.onDelete(/\/admin\/shipping-templates\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const idx = shippingTemplates.findIndex((t) => t.id === id);
    if (idx === -1) return fail('运费模板不存在');
    if (shippingTemplates[idx].isDefault) return fail('默认模板不可删除,请先切换默认模板');
    if (products.some((p: any) => p.shippingTemplateId === id)) return fail('该模板已绑定商品,请先解绑后再删除');
    shippingTemplates.splice(idx, 1);
    return ok({ id, removed: true });
  });

  // 导出内存工具供其它 mock 使用
  (setup as { products?: Product[] }).products = products;
}
// @ts-nocheck -- 开发期模拟数据不参与生产 API 契约校验
